import React, { useState, useEffect } from "react";
import { BASE_URL, get, postForm, del } from "../api/config";

const TimelineManager = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [timelines, setTimelines] = useState([]);
  const [formData, setFormData] = useState({
    text: "",
    timeline_date: "",
    images: [],
    document: null,
  });
  const [editingTimeline, setEditingTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch timelines when project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchTimelines(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await get("projects?status_id=2");
      setProjects(response.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      alert("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchTimelines = async (projectId) => {
    try {
      const response = await get(`projects/${projectId}/timeline`);
      setTimelines(response.data || []);
    } catch (error) {
      console.error("Error fetching timelines:", error);
      setTimelines([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
  };

  const handleDocumentChange = (e) => {
    const { files } = e.target;
    setFormData((prev) => ({
      ...prev,
      document: files[0] || null,
    }));
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      alert("Please select a project");
      return;
    }

    if (!formData.timeline_date) {
      alert("Please select a timeline date");
      return;
    }

    try {
      setLoading(true);
      const submitData = new FormData();
      submitData.append("project_id", selectedProject);
      submitData.append("text", formData.text);
      submitData.append("timeline_date", formData.timeline_date);

      // Append multiple images
      formData.images.forEach((image, index) => {
        submitData.append("image[]", image);
      });

      if (formData.document) {
        submitData.append("document", formData.document);
      }

      if (editingTimeline) {
        await postForm(
          `projects/${selectedProject}/timeline/${editingTimeline.timeline_id}`,
          submitData
        );
      } else {
        await postForm(`projects/${selectedProject}/timeline`, submitData);
      }

      // Reset form
      setFormData({
        text: "",
        timeline_date: "",
        images: [],
        document: null,
      });
      setEditingTimeline(null);

      // Reset file inputs
      const imageInput = document.getElementById("image-input");
      const documentInput = document.getElementById("document-input");
      if (imageInput) imageInput.value = "";
      if (documentInput) documentInput.value = "";

      fetchTimelines(selectedProject);

      alert(
        editingTimeline
          ? "Timeline updated successfully!"
          : "Timeline created successfully!"
      );
    } catch (error) {
      console.error("Error saving timeline:", error);
      alert("Failed to save timeline");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (timeline) => {
    setEditingTimeline(timeline);
    setFormData({
      text: timeline.text || "",
      timeline_date: timeline.timeline_date || "",
      images: [],
      document: null,
    });
  };

  const handleDelete = async (timelineId) => {
    if (!window.confirm("Are you sure you want to delete this timeline?")) {
      return;
    }

    try {
      await del(`projects/${selectedProject}/timeline/${timelineId}`);
      fetchTimelines(selectedProject);
      alert("Timeline deleted successfully!");
    } catch (error) {
      console.error("Error deleting timeline:", error);
      alert("Failed to delete timeline");
    }
  };

  const cancelEdit = () => {
    setEditingTimeline(null);
    setFormData({
      text: "",
      timeline_date: "",
      images: [],
      document: null,
    });
    const imageInput = document.getElementById("image-input");
    const documentInput = document.getElementById("document-input");
    if (imageInput) imageInput.value = "";
    if (documentInput) documentInput.value = "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    return "document";
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Project Timeline Manager
        </h1>
        <p className="text-gray-600">
          Manage project timelines with multiple media uploads
        </p>
      </div>

      {/* Project Selection */}
      <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <label className="block text-lg font-semibold text-gray-800 mb-3">
          📁 Select Project
        </label>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        >
          <option value="">Choose a project...</option>
          {projects.map((project) => (
            <option key={project.project_id} value={project.project_id}>
              {project.project_name} (ID: {project.project_id}) -{" "}
              {project.location}
            </option>
          ))}
        </select>
      </div>

      {/* Timeline Form */}
      {selectedProject && (
        <form
          onSubmit={handleSubmit}
          className="mb-8 bg-white p-6 rounded-xl border-2 border-gray-100 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              {editingTimeline ? "✏️" : "➕"}
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              {editingTimeline ? "Edit Timeline" : "Add New Timeline Entry"}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📅 Timeline Date *
              </label>
              <input
                type="date"
                name="timeline_date"
                value={formData.timeline_date}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📝 Description *
              </label>
              <textarea
                name="text"
                value={formData.text}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="Describe what happened in this timeline entry..."
              />
            </div>
          </div>

          {/* Image/Video Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              🖼️ Upload Images & Videos
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors duration-200">
              <input
                id="image-input"
                type="file"
                multiple
                onChange={handleImageChange}
                accept="image/*,video/*"
                className="hidden"
              />
              <label htmlFor="image-input" className="cursor-pointer">
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <span className="text-2xl">📁</span>
                  </div>
                  <p className="text-gray-700 font-medium">
                    Click to upload images/videos
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports multiple files (JPEG, PNG, MP4, MOV, etc.)
                  </p>
                </div>
              </label>
            </div>

            {/* Selected Images Preview */}
            {formData.images.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Selected Files ({formData.images.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {formData.images.map((file, index) => (
                    <div
                      key={index}
                      className="relative group border rounded-lg p-3 bg-gray-50"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm">
                          {getFileType(file) === "image" ? "🖼️" : "🎥"}
                        </span>
                        <span className="text-xs font-medium truncate flex-1">
                          {file.name}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {formatFileSize(file.size)}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Document Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              📄 Upload Document
            </label>
            <div className="flex items-center gap-4">
              <input
                id="document-input"
                type="file"
                onChange={handleDocumentChange}
                accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            {formData.document && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-lg">📄</span>
                  <div>
                    <p className="font-medium text-sm">
                      {formData.document.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(formData.document.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, document: null }))
                  }
                  className="text-red-500 hover:text-red-700 text-lg font-bold"
                >
                  ×
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Supports PDF, DOC, DOCX, TXT, Excel files
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  {editingTimeline
                    ? "💾 Update Timeline"
                    : "✅ Create Timeline"}
                </>
              )}
            </button>
            {editingTimeline && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
              >
                ❌ Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {/* Timelines List */}
      {selectedProject && (
        <div className="bg-white rounded-xl border-2 border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-lg">📋</span>
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              Project Timelines
            </h2>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
              {timelines.length} entries
            </span>
          </div>

          {timelines.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-500 text-lg font-medium">
                No timelines found for this project.
              </p>
              <p className="text-gray-400 mt-2">
                Create your first timeline entry above!
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {timelines.map((timeline) => (
                <div
                  key={timeline.timeline_id}
                  className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-200 bg-white group"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                          📅{" "}
                          {new Date(
                            timeline.timeline_date
                          ).toLocaleDateString()}
                        </div>
                        <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                          Created:{" "}
                          {new Date(timeline.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <p className="text-gray-800 text-lg leading-relaxed">
                        {timeline.text}
                      </p>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleEdit(timeline)}
                        className="px-4 py-2 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 flex items-center gap-2"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(timeline.timeline_id)}
                        className="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 flex items-center gap-2"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>

                  {/* Media Attachments */}
                  {(timeline.images && timeline.images.length > 0) ||
                  timeline.document ? (
                    <div className="pt-4 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Attachments
                      </h4>

                      {/* Images */}
                      {timeline.images && timeline.images.length > 0 && (
                        <div className="mb-3">
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {timeline.images.map((image, index) => (
                              <a
                                key={index}
                                href={`${BASE_URL}${image}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block border-2 border-gray-200 rounded-lg p-3 hover:border-blue-400 transition-colors duration-200 group"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">
                                    {image.includes(".mp4") ||
                                    image.includes(".mov")
                                      ? "🎥"
                                      : "🖼️"}
                                  </span>
                                  <span className="text-xs font-medium truncate flex-1">
                                    File {index + 1}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1 text-center group-hover:text-blue-600">
                                  View File
                                </p>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Document */}
                      {timeline.document && (
                        <div>
                          <a
                            href={`${BASE_URL}${timeline.document}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors duration-200"
                          >
                            <span className="text-lg">📄</span>
                            <span className="font-medium">
                              Download Document
                            </span>
                          </a>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineManager;
